import moment from 'moment';
import convertToCelsius from '@shared/lib/converter/convertToCelsius';
import convertToCelsiusResult from '@shared/lib/converter/convertToCelsiusResult';
import convertToDayMonth from '@shared/lib/converter/convertToDayMonth';
import formatCycleRuntime from '@shared/lib/formatter/formatCycleRuntime';
import mutations from './serviceMutation';
import actions from './serviceAction';
import initialState from './serviceInitialState';

const getters = {
  recentCycleTableData: (state, getter, rootState) => {
    const {
      admin: {
        partnerSettings: {
          TEMP_UNIT = '°F',
          PRESSURE_UNIT = 'inH2O',
          DATE_FORMAT = 'MM-DD-YYYY',
        },
      },
    } = rootState;
    const {
      recentCycleList,
      pressureConversion,
    } = state;
    if (recentCycleList) {
      return recentCycleList.map((c) => {
        const {
          acMode,
          tempAPI,
          rhAPI,
          deltaT,
          pressureChange,
          startTime,
          cycleTimeSeconds,
          dtPayload,
        } = c;

        let deltaP = 0;
        if (pressureChange && pressureChange.onChange) {
          if (PRESSURE_UNIT === 'inH2O') {
            deltaP = `${Math.abs(pressureChange.onChange / pressureConversion).toFixed(2)}inH2O`;
          } else {
            deltaP = `${pressureChange.onChange}Pa`;
          }
        }

        let rh;
        if (!rhAPI) {
          rh = '--';
        } else {
          rh = rhAPI;
        }

        let oaTemp = tempAPI;
        let performance = deltaT;
        let supplyAirTemp = dtPayload?.sup;
        let returnAirTemp = dtPayload?.ret;
        if (TEMP_UNIT === '°C') {
          oaTemp = convertToCelsius(tempAPI);
          performance = convertToCelsiusResult(deltaT);
          supplyAirTemp = convertToCelsius(supplyAirTemp);
          returnAirTemp = convertToCelsius(returnAirTemp);
        }
        let date = moment(startTime).format('M-D-YYYY');
        if (DATE_FORMAT === 'DD-MM-YYYY') {
          date = convertToDayMonth(startTime);
        }

        const cd = {
          oaTemp: `${oaTemp}${TEMP_UNIT}/${rh}`,
          deltaP,
          time: moment(startTime).format('h:mmA'),
          runtime: formatCycleRuntime(cycleTimeSeconds),
          date,
          supRet: `${(supplyAirTemp).toFixed(0)}${TEMP_UNIT}/${(returnAirTemp).toFixed(0)}${TEMP_UNIT}`,
        };

        if (acMode === 'Cool') {
          cd.performance = `-${performance.toFixed(0)}${TEMP_UNIT}`;
        } else if (acMode === 'Heat') {
          cd.performance = `+${performance.toFixed(0)}${TEMP_UNIT}`;
        } else {
          cd.performance = `${performance.toFixed(0)}${TEMP_UNIT}`;
        }
        return cd;
      });
    }
    return [];
  },
  pastAlerts: (state, getter, rootState) => {
    const {
      admin: {
        partnerSettings: {
          DATE_FORMAT = 'MM-DD-YYYY',
        },
      },
    } = rootState;
    const {
      pastAlertList,
    } = state;
    const leakList = [];
    const restAlertList = [];
    if (pastAlertList.length) {
      pastAlertList.forEach((alert) => {
        let startTime = moment(alert.startTime).format('M-D-YYYY');
        let firstOccurred = moment(alert.firstOccurred).format('M-D-YYYY');
        let lastOccurred = moment(alert.lastOccurred).format('M-D-YYYY');
        if (DATE_FORMAT === 'DD-MM-YYYY') {
          startTime = convertToDayMonth(alert.startTime);
          firstOccurred = convertToDayMonth(alert.firstOccurred);
          lastOccurred = convertToDayMonth(alert.lastOccurred);
        }
        if (alert.type === 'Water Detected') {
          leakList.push({
            location: alert.location,
            startTime,
            firstOccurred,
            lastOccurred,
            serial: alert.serialNumber.slice(-7),
          });
        } else {
          restAlertList.push({
            type: alert.type,
            title: alert.title,
            systemName: alert.systemName,
            serial: alert.serialNumber.slice(-7),
            location: alert.location,
            startTime,
            firstOccurred,
            lastOccurred,
          });
        }
      });
    }
    return {
      leakList,
      restAlertList,
    };
  },
  pastServicesTableData: (state) => {
    const {
      pastServiceList,
    } = state;
    return pastServiceList.map((ps) => ({
      date: moment(ps.completedDate).format('l') || '--',
      name: ps.assignedTech || '--',
      serviceType: ps.serviceType || '--',
      serviceIssues: ps.serviceIssues || '--',
    }));
  },
  srList: (state) => {
    const {
      serviceRequestList,
      serviceSearch,
    } = state;
    const newList = [];
    serviceRequestList.forEach((item) => {
      let matchesSearch = true;
      if (serviceSearch) {
        const searchTerm = serviceSearch.toLowerCase();
        const name = `${item.customerName}`.trim();
        const { email, phone, corpName } = item;
        matchesSearch = (
          (name && name.toLowerCase().includes(searchTerm))
          || (email && email.toLowerCase().includes(searchTerm))
          || (phone && phone.replaceAll('-', '').toLowerCase().includes(searchTerm))
          || (corpName && corpName.toLowerCase().includes(searchTerm))
        );
      }
      if (matchesSearch) {
        newList.push(item);
      }
    });
    return newList;
  },
  actionJobList: (state) => {
    const {
      jobList,
      serviceSearch,
    } = state;
    const rescheduleList = [];
    const cancelList = [];
    jobList.forEach((item) => {
      let matchesSearch = true;
      if (serviceSearch) {
        const searchTerm = serviceSearch.toLowerCase();
        const name = item.customerName;
        const { corpName } = item;
        matchesSearch = (
          (name && name.toLowerCase().includes(searchTerm))
          || (corpName && corpName.toLowerCase().includes(searchTerm))
        );
      }
      const status = item.serviceStatus;
      if (status === 'Reschedule Request' && matchesSearch) {
        rescheduleList.push(item);
      } else if (status === 'Cancellation Pending' && matchesSearch) {
        cancelList.push(item);
      }
    });
    return {
      rescheduleList,
      cancelList,
    };
  },
  leadList: (state) => {
    const {
      leadList,
      serviceSearch,
    } = state;
    const newList = [];
    leadList.forEach((item) => {
      let matchesSearch = true;
      if (serviceSearch) {
        const search = serviceSearch.toLowerCase();
        const {
          customerName,
          type,
          title,
          serviceAddress: {
            address1,
            city,
            zip,
          },
          uid,
          manufacturer,
          refrigerant,
        } = item;

        const address = `${address1} ${city} ${zip}`;
        const allSearchParams = `${customerName} ${type} ${title} ${address} ${uid} ${manufacturer} ${refrigerant}`;
        const searchString = allSearchParams.toLowerCase().toString();
        matchesSearch = !!searchString.match(search);
      }
      if (matchesSearch) {
        newList.push(item);
      }
    });
    return newList;
  },
  jobListFiltered: (state) => {
    const {
      jobList,
      serviceSearch,
    } = state;
    const newList = [];
    jobList.forEach((item) => {
      let matchesSearch = true;
      if (serviceSearch) {
        const searchTerm = serviceSearch.toLowerCase();
        const name = item.customerName;
        const { corpName } = item;
        matchesSearch = (
          (name && name.toLowerCase().includes(searchTerm))
          || (corpName && corpName.toLowerCase().includes(searchTerm))
        );
      }
      if (matchesSearch) {
        newList.push(item);
      }
    });
    return newList;
  },
};
const serviceModule = {
  state: initialState(),
  mutations,
  actions,
  getters,
};
export default serviceModule;
